import React, { useState, useMemo, useEffect, useRef } from "react";
import { Modal, Spin } from "antd";
import Button2 from "../../../NewComponent/Button2/Button2";
import TooltipComp from "../../../NewComponent/ToolTip/TooltipComp";
import { ReactComponent as PdfWhiteIcon } from "../../../Icons/downloadDark.svg";
import { ReactComponent as CopyIcon } from "../../../Icons/copyDark.svg";
import { ReactComponent as SettingsIcon } from "../../../Icons/settings.svg";
import { ReactComponent as HeaderDocIcon } from "../../../Icons/pdfWhiteIcon.svg";
import { ReactComponent as CrossIcon } from "../../../Icons/cross.svg";
import { ReactComponent as SubmitIcon } from "../../../Icons/submit.svg";
import { ReactComponent as ApprovedIcon } from "../../../Icons/approvedDark.svg";
import { ReactComponent as AwaitingIcon } from "../../../Icons/awaitingDark.svg";
import Content from "./Content";
import styles from "./BriefModal.module.css";

export default function BriefModal({
  open,
  onClose,
  resp,
  isStep,
  onToggleSteps,
  audioUrl,
  onPlayAudio,
  onConvertPdf,
  onCopy,
  onSubmitForReview,
  reviewed,
  submittedForReview,
  language,
  contentRef,
  currentRole,
  onClickEdit,
  loading
}) {
  const scrollRef = useRef(null);
  const audioRef = useRef(null);

  // auto-reveal when audio appears:
  useEffect(() => {
    if (audioUrl && scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [audioUrl]);

  // Settings panel open by default each time modal opens
  const [showSettings, setShowSettings] = useState(true);
  useEffect(() => {
    if (open) setShowSettings(true);
  }, [open]);

  const hasAudio = useMemo(() => !!resp?.audioFileName, [resp]);

  const stopAllAudio = () => {
    audioRef.current?.pause();
    if (audioRef.current) audioRef.current.currentTime = 0;
  };
  const handleClose = () => {
    stopAllAudio();
    onClose();
  };
  // review state
  const canSubmitForReview =
    resp &&
    resp.status === "completed" &&
    language === "English" &&
    reviewed === false &&
    submittedForReview === false;

  const awaitingReview =
    submittedForReview === true && reviewed === false && language === "English";

  const isNonEnglish = language && language !== "English";
  const isSeeker = currentRole === "Seeker";
  const isExpert = currentRole === "Expert";
  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      centered
      width={Math.min(1200, window.innerWidth - 80)}
      maskClosable={false}
      title={null}
      closable={false}
      bodyStyle={{ padding: 0, borderRadius: 16, overflow: "hidden" }}
      wrapClassName={styles.vizertoModalWrap}
    >
      {/* Gradient header with the actual brief question */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <HeaderDocIcon className={styles.headerDocIcon} />
          <span className={styles.headerTitle} title={resp?.userQuery || ""}>
            {resp?.userQuery || "—"}
          </span>
        </div>

        {/* Close circle */}
        <button
          className={styles.headerClose}
          onClick={handleClose}
          aria-label="Close"
          title="Close"
        >
          <span className={styles.closeCircle}>
            <CrossIcon className={styles.closeIcon} />
          </span>
        </button>
      </div>

      {/* Positioning root for floating pill */}
      <div className={styles.modalRoot}>
        {/* Body */}
        <Spin spinning={loading}>
          <div className={styles.contentArea}>
          <div className={styles.paperCard}>
            {!isStep ? (
              <div ref={contentRef} className={styles.briefHtml}>
                <Content text={resp?.briefOutput ?? ""} />
              </div>
            ) : (
              <div
                ref={contentRef}
                className={styles.briefHtml}
                dangerouslySetInnerHTML={{
                  __html: resp?.processingSteps ?? "",
                }}
              />
            )}
          </div>
        </div>
        </Spin>

        {/* Floating full-width pill (gear + actions in one line) */}
        <div
          className={`${styles.controlsPill} ${
            !showSettings ? styles.collapsed : ""
          }`}
          role="group"
          aria-label="Brief actions"
        >
          {/* Gear toggles the actions visibility */}
          <button
            className={styles.pillGear}
            onClick={() => setShowSettings((s) => !s)}
            aria-expanded={showSettings}
            aria-controls="brief-controls"
            title="Settings"
          >
            <SettingsIcon />
          </button>

          {showSettings && (
            <div
              id="brief-controls"
              className={`${styles.controlsRow} ${
                !showSettings ? styles.hiddenRow : ""
              }`}
            >
              {/* Submit/Awaiting/Reviewed */}
              {isExpert && !resp?.answered  && language === "English" && (
                <Button2 onClickFunc={onClickEdit}>
                  <i className="fa fa-edit" style={{color:'#5c667b', fontSize:'16px'}}></i>
                  &nbsp;Edit Brief
                </Button2>
              )}
              {isSeeker && canSubmitForReview && (
                <Button2 onClickFunc={onSubmitForReview}>
                  <SubmitIcon className={styles.btnIcon} />
                  &nbsp;Submit for Review
                </Button2>
              )}
              {isSeeker && awaitingReview && (
                <Button2 disabled onClickFunc={() => {}}>
                  <AwaitingIcon className={styles.btnIcon} />
                  &nbsp;Awaiting Review
                </Button2>
              )}
              {isSeeker && reviewed && (
                <Button2 disabled onClickFunc={() => {}}>
                  <ApprovedIcon className={styles.btnIcon} />
                  &nbsp;Reviewed
                </Button2>
              )}

              {/* Non-English disabled with tooltip */}
              {isSeeker && isNonEnglish && !reviewed && (
                <TooltipComp
                  title="Expert review limited to English briefs at this time"
                  getPopupContainer={() => document.body}
                  overlayStyle={{ zIndex: 20000 }}
                  destroyTooltipOnHide
                >
                  {/* <div style={{ opacity: 0.75 }}> */}
                  <span style={{ display: "inline-block", opacity: 0.75 }}>
                    <Button2 disabled onClickFunc={() => {}}>
                      <SubmitIcon className={styles.btnIcon} />
                      &nbsp;Submit for Review
                    </Button2>
                  </span>
                  {/* </div> */}
                </TooltipComp>
              )}

              {/*  Play Audio */}
              {hasAudio && (
                <Button2 onClickFunc={onPlayAudio}>
                  <i
                    className="fa fa-volume-up "
                    style={{
                      fontSize: 18,
                      lineHeight: 1,
                      verticalAlign: "-2px",
                    }}
                  />{" "}
                  &nbsp;Play Audio
                </Button2>
              )}

              {/* Steps toggle */}
              <Button2 onClickFunc={onToggleSteps}>
                <img
                  src={require("../../../Images/foot.png")}
                  alt="steps"
                  style={{ width: 18, height: 18, verticalAlign: "-2px" }}
                />
                &nbsp;{isStep ? "Hide Steps" : "Show Steps"}
              </Button2>

              {/* Download + Copy */}
              <Button2 onClickFunc={onConvertPdf}>
                <PdfWhiteIcon className={styles.btnIcon} />
                &nbsp;Download PDF
              </Button2>
              <Button2 onClickFunc={onCopy}>
                <CopyIcon className={styles.btnIcon} />
                &nbsp;Copy
              </Button2>
              {/* audio on the right of the pill */}
              {audioUrl && (
                <div style={{ marginLeft: "auto" }}>
                  <audio
                    ref={audioRef}
                    controls
                    src={audioUrl}
                    style={{ height: 36, maxWidth: 320 }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
